import { z } from 'zod'

/**
 * **Wann eine Abnahme-Erinnerung fällig ist** ([Z-09](../../../docs/phasen/zusatz-09-abnahme-erinnerung.md)).
 *
 * Nils, 25.08.2026: *„Können wir eigentlich den Leuten eine Mail schicken 1x die Woche mit den
 * ‚Abzunehmenden' Aufgaben? Wenn da welche sind? Also als Erinnerung? … Die Häufigkeit sollte
 * man einstellen können (am besten pro Benutzer jeder selber), Standard 1x mittwochs."*
 *
 * ## Warum das hier steht und nicht im Zeitgeber
 *
 * Weil es sonst nur **im Betrieb** prüfbar wäre – und zwar frühestens am nächsten Mittwoch.
 * Eine Regel, für deren Prüfung man eine Woche warten muss, wird nicht geprüft. Als reine
 * Funktion mit übergebenem Stichtag ist sie eine Tabelle aus Fällen, und der Zeitgeber ist
 * danach eine Schleife, die sie fragt.
 *
 * Dieselbe Aufteilung wie bei `schirm.ts` (Z-03) und `meintag.ts` (Z-02) – und dieselbe
 * eiserne Regel: **kein `new Date()` ohne Übergabe.**
 */

/** Die Takte, die zur Auswahl stehen. */
export const ERINNERUNGS_TAKTE = ['aus', 'taeglich', 'woechentlich'] as const
export type ErinnerungsTakt = (typeof ERINNERUNGS_TAKTE)[number]
export const erinnerungsTaktSchema = z.enum(ERINNERUNGS_TAKTE)

export const ERINNERUNGS_TAKT_LABELS: Record<ErinnerungsTakt, string> = {
  aus: 'Keine Erinnerung',
  taeglich: 'Täglich',
  woechentlich: 'Wöchentlich',
}

/**
 * **Der Wochentag als ISO-Zahl: 1 = Montag … 7 = Sonntag.**
 *
 * Nicht `Date.getDay()`s Zählung (0 = Sonntag). Die läuft der Woche voraus statt hinterher und
 * ist die häufigste Quelle für „einen Tag daneben" – gerade beim Rückwärtsrechnen weiter unten.
 */
export const WOCHENTAGE: readonly { wert: number; kurz: string; lang: string }[] = [
  { wert: 1, kurz: 'Mo', lang: 'Montag' },
  { wert: 2, kurz: 'Di', lang: 'Dienstag' },
  { wert: 3, kurz: 'Mi', lang: 'Mittwoch' },
  { wert: 4, kurz: 'Do', lang: 'Donnerstag' },
  { wert: 5, kurz: 'Fr', lang: 'Freitag' },
  { wert: 6, kurz: 'Sa', lang: 'Samstag' },
  { wert: 7, kurz: 'So', lang: 'Sonntag' },
]

/** Voreinstellung: **wöchentlich, mittwochs** – so beauftragt. */
export const ERINNERUNG_STANDARD_TAKT: ErinnerungsTakt = 'woechentlich'
export const ERINNERUNG_STANDARD_TAG = 3

/**
 * **„Sofort, sobald etwas zur Abnahme kommt"** – der zweite Kanal
 * ([E-302](../../../docs/ENTSCHEIDUNGEN.md#e-302)).
 *
 * Nils, 02.09.2026: *„Und die Abnahmen doch verschicken sobald die auftauchen (Die wollen
 * schneller abnehmen)."*
 *
 * ## Warum ein eigener Schalter und kein vierter Takt
 *
 * Weil es eine **andere Frage** ist. Der Takt beantwortet *„wie oft bekomme ich die Liste
 * dessen, was offen ist"*; dieser Schalter beantwortet *„höre ich es, sobald etwas Neues
 * dazukommt"*. Als vierter Takt („sofort") stünden beide in einer Auswahl, und wer ihn wählte,
 * verlöre stillschweigend die Zusammenfassung – also genau die Mail, die den Rückstand zeigt.
 *
 * Die Sofortmeldung nennt **nur, was neu ist**. Der Takt nennt **alles, was offen ist**. Zwei
 * Mails mit demselben Inhalt wären eine zu viel; zwei mit verschiedenem sind zwei Antworten.
 *
 * **Voreingestellt an**, weil es der ausdrückliche Auftrag ist – wer die Ruhe will, schaltet
 * ihn ab, und der Takt bleibt trotzdem stehen.
 */
export const ERINNERUNG_STANDARD_SOFORT = true

/**
 * **Zu welcher Uhrzeit.**
 *
 * Bewusst **nicht** einstellbar. Nils wollte die *Häufigkeit* pro Person wählbar haben; eine
 * Uhrzeit dazu wäre eine zweite Stellschraube, die niemand verstellt und die jede Fehlersuche
 * verdoppelt („bei mir kam nichts" – „wann steht deine Uhrzeit?"). Sieben Uhr heißt: die Mail
 * liegt da, bevor der Tag anfängt.
 */
export const ERINNERUNG_STUNDE = 7

export interface Erinnerungseinstellung {
  takt: ErinnerungsTakt
  /** Nur bei `woechentlich` von Bedeutung. ISO: 1 = Montag … 7 = Sonntag. */
  wochentag: number
}

/** ISO-Wochentag eines Datums: 1 = Montag … 7 = Sonntag. */
export function isoWochentag(zeitpunkt: Date): number {
  const tag = zeitpunkt.getDay()
  return tag === 0 ? 7 : tag
}

/**
 * **Der letzte Zeitpunkt, zu dem die Erinnerung fällig war** – oder `null`, wenn nie.
 *
 * Rückwärts gerechnet, nicht vorwärts, und das ist der Kern:
 *
 * > Ein Takt, der vorwärts rechnet („der nächste Mittwoch"), **verschluckt** einen Lauf, wenn
 * > der Prozess am Mittwoch stand. Er springt dann auf den übernächsten, und niemand merkt es –
 * > eine Erinnerung, die manchmal ausfällt, ist schlimmer als keine.
 *
 * Rückwärts gefragt lautet dieselbe Sache: *seit wann ist es überfällig?* Und darauf gibt es
 * auch am Donnerstag eine Antwort. Wer den Mittwoch verpasst hat, bekommt die Mail eben am
 * Donnerstag – einen Tag zu spät ist unendlich viel besser als gar nicht.
 */
export function letzteFaelligkeit(einstellung: Erinnerungseinstellung, jetzt: Date): Date | null {
  if (einstellung.takt === 'aus') return null

  /** Heute zur festen Stunde. Liegt das noch in der Zukunft, zählt erst der Vortag. */
  const heute = new Date(jetzt)
  heute.setHours(ERINNERUNG_STUNDE, 0, 0, 0)

  if (einstellung.takt === 'taeglich') {
    if (heute <= jetzt) return heute
    const gestern = new Date(heute)
    gestern.setDate(gestern.getDate() - 1)
    return gestern
  }

  /**
   * Wöchentlich: vom heutigen Tag aus höchstens sieben Tage zurückgehen, bis der gewünschte
   * Wochentag getroffen ist **und** der Zeitpunkt nicht in der Zukunft liegt.
   */
  const ziel = new Date(heute)
  for (let i = 0; i <= 7; i++) {
    if (isoWochentag(ziel) === einstellung.wochentag && ziel <= jetzt) return ziel
    ziel.setDate(ziel.getDate() - 1)
  }
  return null
}

/**
 * **Ist eine Erinnerung fällig?**
 *
 * `zuletzt` ist die Wasserstandsmarke: wann der Lauf für diese Person zuletzt **stattgefunden**
 * hat – nicht, wann zuletzt eine Mail hinausging.
 *
 * ## Warum die Marke auch dann gesetzt wird, wenn nichts zu tun war
 *
 * Weil sonst die Zusage „einmal mittwochs" bricht. Ohne Marke prüft der Takt immer weiter, und
 * die erste Aufgabe, die am Donnerstagnachmittag in die Abnahme rutscht, löst **sofort** eine
 * Mail aus. Aus „einmal die Woche" würde „immer dann, wenn etwas passiert" – also genau die
 * Sorte Benachrichtigung, die man nach zwei Wochen wegfiltert.
 */
export function erinnerungFaellig(
  einstellung: Erinnerungseinstellung,
  jetzt: Date,
  zuletzt: Date | null,
): boolean {
  const faellig = letzteFaelligkeit(einstellung, jetzt)
  if (faellig === null) return false
  if (zuletzt === null) return true
  return zuletzt < faellig
}

/** „Wöchentlich, mittwochs" – für die Oberfläche und für die Mail selbst. */
export function erinnerungBeschreiben(einstellung: Erinnerungseinstellung): string {
  if (einstellung.takt === 'aus') return 'Keine Erinnerung'
  if (einstellung.takt === 'taeglich') return `Täglich um ${ERINNERUNG_STUNDE} Uhr`
  const tag = WOCHENTAGE.find((w) => w.wert === einstellung.wochentag)
  return `Wöchentlich ${tag?.lang ?? 'mittwochs'} um ${ERINNERUNG_STUNDE} Uhr`
}

/**
 * **Was der Mensch über seine Abnahme-Mails erfährt** – Takt und Sofortmeldung in einem Satz.
 *
 * Eigene Funktion neben {@link erinnerungBeschreiben} und nicht statt ihr: die Takt-Beschreibung
 * ist die Antwort auf *„wann kommt die Liste"* und steht auch dort, wo es keine Sofortmeldung
 * gibt. Hier stehen beide Kanäle, weil beide zusammen die vollständige Auskunft sind – und die
 * gehört an den Fuß der Mail und an die Zeile unter dem eigenen Namen, an beiden Stellen
 * wortgleich.
 */
export function abnahmeMeldungBeschreiben(
  einstellung: Erinnerungseinstellung,
  sofort: boolean,
): string {
  const takt = erinnerungBeschreiben(einstellung)
  if (!sofort) return takt
  if (einstellung.takt === 'aus') return 'Sofort, sobald etwas zur Abnahme kommt'
  return `Sofort bei Neuem, dazu ${takt.charAt(0).toLowerCase()}${takt.slice(1)}`
}
